import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NAV } from "@/config/nav";
import { useRole, useRoleQuery } from "@/api/hooks";
import { AutoFileDialog } from "@/components/shared/AutoFileDialog";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem,
  CommandList, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { Boxes, FileText, UploadCloud, UserRound } from "lucide-react";

const asList = (value) => Array.isArray(value) ? value : (value?.items || value?.data || []);

export function CommandCenter({ open, onOpenChange }) {
  const role = useRole();
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const canSearchCustomers = role !== "analytics";
  const canSearchJobs = ["sales", "operations", "finance", "management"].includes(role);
  const canSearchQuotes = role === "sales";
  const { data: customers } = useRoleQuery(["command-customers"], "/customers", { enabled: open && canSearchCustomers, staleTime: 60000 });
  const { data: jobs } = useRoleQuery(["command-jobs"], "/jobs", { enabled: open && canSearchJobs, staleTime: 60000, retry: false });
  const { data: quotations } = useRoleQuery(["command-quotes"], "/quotations", { enabled: open && canSearchQuotes, staleTime: 60000, retry: false });

  useEffect(() => {
    const handleKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onOpenChange]);

  const customerRows = useMemo(() => asList(customers).slice(0, 30), [customers]);
  const jobRows = useMemo(() => asList(jobs).slice(0, 30), [jobs]);
  const quoteRows = useMemo(() => asList(quotations).slice(0, 30), [quotations]);
  const go = (path) => { onOpenChange(false); navigate(path); };
  const customerPath = (id) => `/${role}/customers/${id}`;
  const jobPath = (id) => `/${role}/jobs/${id}`;

  return (
    <>
      <CommandDialog open={open} onOpenChange={onOpenChange}>
        <CommandInput autoFocus placeholder="Search records or jump to a page…" aria-label="Search the workspace" />
        <CommandList className="max-h-[430px]">
          <CommandEmpty>No matching pages or records found.</CommandEmpty>
          <CommandGroup heading="Workspace">
            {(NAV[role] || []).map((item, index) => (
              <CommandItem key={item.to} value={`page ${item.label}`} onSelect={() => go(item.to)}>
                <item.icon />
                <span>{item.label}</span>
                {index < 9 && <CommandShortcut>G {index + 1}</CommandShortcut>}
              </CommandItem>
            ))}
          </CommandGroup>
          {role !== "analytics" && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Quick actions">
                <CommandItem value="upload document review" onSelect={() => { onOpenChange(false); setUploadOpen(true); }}>
                  <UploadCloud /><span>Upload document for review</span>
                </CommandItem>
              </CommandGroup>
            </>
          )}
          {customerRows.length > 0 && canSearchCustomers && (
            <CommandGroup heading="Customers">
              {customerRows.map((customer) => (
                <CommandItem key={customer.id} value={`customer ${customer.name || customer.company || ""} ${customer.id}`} onSelect={() => go(customerPath(customer.id))}>
                  <UserRound /><span className="truncate">{customer.name || customer.company || customer.id}</span>
                  <CommandShortcut>{customer.id}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {jobRows.length > 0 && canSearchJobs && (
            <CommandGroup heading="Jobs">
              {jobRows.map((job) => (
                <CommandItem key={job.id} value={`job ${job.id} ${job.customer || job.customerName || ""} ${job.origin || ""} ${job.destination || ""}`} onSelect={() => go(jobPath(job.id))}>
                  <Boxes /><span className="truncate">{job.id} · {job.customer || job.customerName || "Unknown customer"}</span>
                  <CommandShortcut>{job.status}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {quoteRows.length > 0 && canSearchQuotes && (
            <CommandGroup heading="Quotations">
              {quoteRows.map((quote) => (
                <CommandItem key={quote.id} value={`quotation quote ${quote.id} ${quote.customer || quote.customerName || ""}`} onSelect={() => go(`/sales/quotations/${quote.id}`)}>
                  <FileText /><span className="truncate">{quote.id} · {quote.customer || quote.customerName || "Unknown customer"}</span>
                  <CommandShortcut>{quote.status}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
        <div className="flex items-center justify-between border-t bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
          <span>Use ↑↓ to move · Enter to open</span><span>Esc to close</span>
        </div>
      </CommandDialog>
      <AutoFileDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </>
  );
}
